<template>
  <es-loader
    v-if="loading"
    style="margin: 6.5rem"
  />
  <es-exception
    v-else-if="loadingError"
    exceptionMessage="There was an issue. Please contact the admin."
    fontSize="16px"
    iconSize="4x"
    margin="0rem"
    style="padding: 15rem"
  />
  <div v-else>
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              Shared ID Group Details
            </div>
          </template>
        </es-header>
      </div>
      <es-exception
        v-if="personTypeMisMatched"
        exceptionMessage="The identifier split request is not authorized.
          The identifier is not the same person type and cannot be split."
        fontSize="16px"
        iconSize="4x"
        margin="0rem"
        style="padding: 14rem"
      />
      <es-exception
        v-if="!personTypeAccess"
        :exceptionMessage="personTypeAccessErrorMessage"
        fontSize="16px"
        iconSize="4x"
        margin="0rem"
        style="padding: 14rem"
      />
      <es-exception
        v-else-if="dataAccessPermission"
        :exceptionMessage="dataAccessErrorMessage"
        fontSize="16px"
        iconSize="4x"
        margin="0rem"
        style="padding: 14rem"
      />
      <div v-else>
        <div>
          <div style="padding: 1rem">
            <div class="outer">
              <table class="table">
                <thead>
                  <tr style="height: 42px" v-if="!splitConfirmed">
                    <th style="border: 0" rowspan="2">
                      <div style="font-size: 20px; font-weight: 500; color: #000">
                        SPLITTING IDs
                      </div>
                      <div style="font-size: 12px; color: #000">
                        A new identifier will be created.
                      </div>
                    </th>
                    <th class="label-retain data-column" style="background-color: #595959">
                      ID TO BE RETAINED
                    </th>
                    <th class="blank-column icon-cell"/>
                    <th
                      class="label-newid data-column"
                    >
                    ID TO BE SPLIT (NEW ID TO BE CREATED)
                    </th>
                  </tr>
                  <tr style="height: 42px" v-else>
                    <th style="border: 0" rowspan="2"/>
                    <th class="label-retain data-column" style="background-color: #5C5CB3">
                      RETAINED ID
                    </th>
                    <th class="blank-column icon-cell"/>
                    <th
                      class="label-newid data-column"
                    >
                    NEW ID
                    </th>
                  </tr>
                  <tr>
                    <!-- <td class="compare-panel vertical-align--initial"/> -->
                    <td class="compare-panel border--left">
                      <div class="text-align--center">
                        <span class="state-id-label">
                          STATE ID :
                        </span>
                        <strong class="state-id">
                          {{
                            formatStateID(idToRetain)
                          }}
                        </strong>
                      </div>
                    </td>
                    <td class="blank-column border--left switch-label">
                      <button
                        v-if="!splitConfirmed"
                        @click="switchTheData"
                        :class="{
                           'disabled': !newIdToCreateIsNotEmpty
                            || !locationAccessPermission}"
                        :disabled="!newIdToCreateIsNotEmpty
                            || !locationAccessPermission"
                        class="button button-link has-underline text-normal"
                      >
                        Switch Records
                      </button>
                    </td>
                    <td class="compare-panel border--left">
                      <div class="text-align--center">
                        <span class="state-id-label">
                          STATE ID :
                        </span>
                        <span class="state-id">
                          <strong v-if="!splitConfirmed">
                            N/A (To be created)
                          </strong>
                          <span v-else class="state-id">
                            {{
                              formatStateID(newPerson.stateStudentId)
                            }}
                          </span>
                        </span>
                      </div>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-if="isMatchingFieldsHeaderEnabled"
                    style="border-bottom: 1px solid #DEE2E6"
                  >
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Matching Fields
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="matchingFieldsInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="matchingFieldsInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showMatchingFields"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleMatchingFields()"
                          @keypress.enter="toggleMatchingFields()"
                          tabindex="0"
                          role="button"
                          title="Hide Details"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleMatchingFields()"
                          @keypress.enter="toggleMatchingFields()"
                          tabindex="0"
                          role="button"
                          title="View Details"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('FIRST_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('FIRST_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.firstName, newIdToCreate.firstName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.firstName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.firstName, newIdToCreate.firstName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.firstName }}
                      </div>
                    </td>
                    <td
                      v-else
                      class="border--left"
                      :rowspan="rowSpanCountNum"
                    >
                      <div class="text-align--center">
                        <div>
                          <font-awesome-icon
                            :icon="['fal', 'exclamation-triangle']"
                            size="3x"
                          />
                        </div>
                        <h6 class="cell-content-no-data">
                          ID To Be Split must be selected to continue the process.
                          Please click the + icon from the History Record.
                        </h6>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('MIDDLE_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('MIDDLE_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.middleName, newIdToCreate.middleName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.middleName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.middleName, newIdToCreate.middleName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.middleName }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('LAST_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('LAST_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.lastName, newIdToCreate.lastName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.lastName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.lastName, newIdToCreate.lastName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.lastName }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('SUFFIX')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SUFFIX') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.suffix, newIdToCreate.suffix)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.suffix }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.suffix, newIdToCreate.suffix)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.suffix }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('PREVIOUS_LAST_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('PREVIOUS_LAST_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.previousLastName, newIdToCreate.previousLastName
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.previousLastName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.previousLastName, newIdToCreate.previousLastName
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.previousLastName }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('GENDER_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('GENDER_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.genderCode, newIdToCreate.genderCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.genderCode, activeGenders) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.genderCode, newIdToCreate.genderCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.genderCode, activeGenders) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('DATE_OF_BIRTH')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('DATE_OF_BIRTH') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.dateOfBirth, newIdToCreate.dateOfBirth)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.dateOfBirth | formatDate }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.dateOfBirth, newIdToCreate.dateOfBirth
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.dateOfBirth | formatDate }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showMatchingFields
                      && fieldEnabled('SOCIAL_SECURITY_NUMBER')
                      && hasModule('PRSID:PRPRF:SSN:VIEW')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.socialSecurityNumber, newIdToCreate.socialSecurityNumber
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.socialSecurityNumber }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      v-if="newIdToCreateIsNotEmpty"
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.socialSecurityNumber, newIdToCreate.socialSecurityNumber
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.socialSecurityNumber }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      History Information
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div class="align--end">
                        <font-awesome-icon
                          v-if="showHistoryInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleHistoryInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleHistoryInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleHistoryInformation()"
                          title="View Details"
                          @keypress.enter="toggleHistoryInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showHistoryInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Number of History Records
                      </div>
                    </td>
                    <td class="compare-panel-field-cell border--left vertical-align--bottom">
                      <div class="cell-content">
                        {{ personToSplitHistInfo.length }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td class="compare-panel-field-cell border--left vertical-align--bottom">
                      <div class="cell-content">
                        {{ newIdToCreateHistInfo.length }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showHistoryInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        History Records
                      </div>
                    </td>
                    <td class="compare-panel-field-cell border--left vertical-align--top">
                      <table role="presentation">
                        <tbody>
                          <tr
                            v-if="cannotSplit"
                            style="border-bottom: 0"
                          >
                            <td>
                              <div class="text-align--center">
                                <font-awesome-icon
                                  :icon="['fas', 'exclamation-triangle']"
                                  size="3x"
                                  style="color: #ffc107!important"
                                />
                                <h6 class="cell-content-no-data">
                                  No History records exist for this Person Record
                                  (State ID {{ formatStateID(idToRetain) }}).
                                  This Identifier cannot be split.
                                </h6>
                              </div>
                            </td>
                          </tr>
                          <tr
                            v-else-if="personToSplitHistInfo.length === 0"
                            style="border-bottom: 0"
                          >
                            <td>
                              <h6 class="cell-content-no-data text-align--center">
                                No History Records have been assigned to this record.
                              </h6>
                            </td>
                          </tr>
                          <tr
                            v-for="(record, index) in personToSplitHistInfo"
                            :key="index"
                          >
                            <td>
                              <div>
                                <strong>
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.lastName, record.lastName,
                                      ),
                                    }"
                                  >
                                    {{ record.lastName }}
                                  </span>
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.firstName, record.firstName,
                                      ),
                                    }"
                                  >
                                    {{ record.firstName }}
                                  </span>
                                  <span
                                    v-if="record.middleName
                                      && (record.middleName !== null || record.middleName !== '')"
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.middleName, record.middleName,
                                      ),
                                    }"
                                  >
                                    {{ record.middleName }}
                                  </span>
                                </strong>
                                <label
                                  v-if="record.socialSecurityNumber
                                    && hasModule('PRSID:PRPRF:SSN:VIEW')"
                                >
                                  ({{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.socialSecurityNumber,
                                        record.socialSecurityNumber,
                                      ),
                                    }"
                                  >
                                    {{ record.socialSecurityNumber }}
                                  </span>
                                  )
                                </label>
                              </div>
                              <div>
                                <label>
                                  {{ fieldLabel('DATE_OF_BIRTH') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.dateOfBirth, record.dateOfBirth,
                                      ),
                                    }"
                                  >
                                    {{ record.dateOfBirth | formatDate }}
                                  </span>
                                </label>
                                <label style="padding-left: 0.5rem">
                                  {{ fieldLabel('GENDER_CODE') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.genderCode, record.genderCode,
                                      ),
                                    }"
                                  >
                                    {{ mapLookUps(record.genderCode, activeGenders) }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  {{ fieldLabel('LOCAL_STUDENT_ID') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.localId, record.localId,
                                      ),
                                    }"
                                  >
                                    {{ record.localId }}
                                  </span>
                                  ({{ mapLookUps(record.sourceCode, activeSources) }})
                                </label>
                                <label style="padding-left: 0.5rem">
                                  Person Type:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.personType, record.personType,
                                      ),
                                    }"
                                  >
                                    {{ mapPersonType(record.personType) }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  Last Transaction ID:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        personToSplit.lastTranSerialNumber,
                                        record.lastTranSerialNumber,
                                      ),
                                    }"
                                  >
                                    {{ record.lastTranSerialNumber }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  Last Updated Date: {{ record.lastUpdateDate | formatLUDate }}
                                </label>
                              </div>
                            </td>
                            <td
                              style="width: 30px"
                              :class="{ 'border--left': !splitConfirmed }"
                            >
                              <div
                                v-if="!splitConfirmed"
                                style="text-align: center"
                              >
                                <button
                                  v-if="!newIdToCreateIsNotEmpty"
                                  :class="{ 'disabled': !locationAccessPermission
                                    || dataAccessPermissionNearMatch }"
                                  :disabled="!locationAccessPermission
                                    || dataAccessPermissionNearMatch"
                                  class="button button-link has-underline"
                                  @click="createNewMaster(record)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'plus']"
                                    title="Create New Master"
                                  />
                                </button>
                                <button
                                  v-else
                                  :class="{ 'disabled': !locationAccessPermission
                                    || dataAccessPermissionNearMatch }"
                                  :disabled="!locationAccessPermission
                                    || dataAccessPermissionNearMatch"
                                  class="button button-link has-underline"
                                  @click="moveHistoryToNewId(record)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'arrow-right-long-to-line']"
                                    title="Move to New ID History"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td class="blank-column border--left"/>
                    <td class="compare-panel-field-cell border--left vertical-align--top">
                      <table role="presentation">
                        <tbody>
                          <tr
                            v-if="newIdToCreateHistInfo.length === 0"
                            style="border-bottom: 0"
                          >
                            <td>
                              <h6 class="cell-content-no-data text-align--center">
                                No History Records have been assigned to this record.1
                              </h6>
                            </td>
                          </tr>
                          <tr
                            v-else
                            v-for="(record, index) in newIdToCreateHistInfo"
                            :key="index"
                          >
                            <td>
                              <div>
                                <strong>
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.lastName, record.lastName,
                                      ),
                                    }"
                                  >
                                    {{ record.lastName }}
                                  </span>
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.firstName, record.firstName,
                                      ),
                                    }"
                                  >
                                    {{ record.firstName }}
                                  </span>
                                  <span
                                    v-if="record.middleName
                                      && (record.middleName !== null || record.middleName !== '')"
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.middleName, record.middleName,
                                      ),
                                    }"
                                  >
                                    {{ record.middleName }}
                                  </span>
                                </strong>
                                <label
                                  v-if="record.socialSecurityNumber
                                    && hasModule('PRSID:PRPRF:SSN:VIEW')"
                                >
                                  ({{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.socialSecurityNumber,
                                        record.socialSecurityNumber,
                                      ),
                                    }"
                                  >
                                    {{ record.socialSecurityNumber }}
                                  </span>
                                  )
                                </label>
                              </div>
                              <div>
                                <label>
                                  {{ fieldLabel('DATE_OF_BIRTH') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.dateOfBirth, record.dateOfBirth,
                                      ),
                                    }"
                                  >
                                    {{ record.dateOfBirth | formatDate }}
                                  </span>
                                </label>
                                <label style="padding-left: 0.5rem">
                                  {{ fieldLabel('GENDER_CODE') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.genderCode, record.genderCode,
                                      ),
                                    }"
                                  >
                                    {{ mapLookUps(record.genderCode, activeGenders) }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  {{ fieldLabel('LOCAL_STUDENT_ID') }}:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.localId, record.localId,
                                      ),
                                    }"
                                  >
                                    {{ record.localId }}
                                  </span>
                                  ({{ mapLookUps(record.sourceCode, activeSources) }})
                                </label>
                                <label style="padding-left: 0.5rem">
                                  Person Type:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.personType, record.personType,
                                      ),
                                    }"
                                  >
                                    {{ mapPersonType(record.personType) }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  Last Transaction ID:
                                  <span
                                    :class="{
                                      'panel--highlighted': compareFields(
                                        newIdToCreate.lastTranSerialNumber,
                                        record.lastTranSerialNumber,
                                      ),
                                    }"
                                  >
                                    {{ record.lastTranSerialNumber }}
                                  </span>
                                </label>
                              </div>
                              <div>
                                <label>
                                  Last Updated Date: {{ record.lastUpdateDate | formatLUDate }}
                                </label>
                              </div>
                            </td>
                            <td
                              style="width: 30px"
                              :class="{ 'border--left': !splitConfirmed }"
                            >
                              <div
                                v-if="!splitConfirmed"
                                style="text-align: center"
                              >
                                <button
                                  :class="{ 'disabled': !locationAccessPermission}"
                                  :disabled="!locationAccessPermission"
                                  class="button button-link has-underline"
                                  @click="moveHistoryToRetainedId(record)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'arrow-left-long-to-line']"
                                    title="Move to Retained ID History"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Alias IDs (Unassigned to a Master or History Record)
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div class="align--end">
                        <font-awesome-icon
                          v-if="showAliasIds"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleAliasIds()"
                          title="Hide Details"
                          @keypress.enter="toggleAliasIds()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleAliasIds()"
                          title="View Details"
                          @keypress.enter="toggleAliasIds()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showAliasIds">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Alias IDs
                      </div>
                    </td>
                    <td class="compare-panel-field-cell border--left vertical-align--top">
                      <table role="presentation">
                        <tbody>
                          <tr
                            v-if="personToSplitAliasIdInfo.length === 0"
                            style="border-bottom: 0"
                          >
                            <td>
                              <h6 class="cell-content-no-data text-align--center">
                                No Alias ID Records have been assigned to this record.
                              </h6>
                            </td>
                          </tr>
                          <tr
                            v-else
                            v-for="(record, index) in personToSplitAliasIdInfo"
                            :key="index"
                          >
                            <td>
                              <div>
                                <strong>
                                  {{ record.aliasId }}
                                </strong>
                                <label>
                                  ({{ record.sourceCode }})
                                </label>
                              </div>
                              <div>
                                Person Type: {{ mapPersonType(record.personType) }}
                              </div>
                              <div>
                                Status:  {{ record.active === true ? 'Active' : 'Inactive' }}
                              </div>
                            </td>
                            <td
                              style="width: 30px"
                              :class="{ 'border--left': !splitConfirmed }"
                            >
                              <div
                                v-if="!splitConfirmed"
                                style="text-align: center"
                              >
                                <button
                                  :disabled="!newIdToCreateIsNotEmpty
                                      || !locationAccessPermission"
                                  :class="{
                                    'disabled': !newIdToCreateIsNotEmpty
                                      || !locationAccessPermission
                                  }"
                                  class="button button-link has-underline"
                                  @click="moveAliasIdToNewId(record)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'arrow-right-long-to-line']"
                                    title="Move to New ID Alias IDs"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td class="blank-column border--left"/>
                    <td class="compare-panel-field-cell border--left vertical-align--top">
                      <table role="presentation">
                        <tbody>
                          <tr
                            v-if="newIdToCreateAliasIdInfo.length === 0"
                            style="border-bottom: 0"
                          >
                            <td>
                              <h6 class="cell-content-no-data text-align--center">
                                No Alias ID Records have been assigned to this record.
                              </h6>
                            </td>
                          </tr>
                          <tr
                            v-else
                            v-for="(record, index) in newIdToCreateAliasIdInfo"
                            :key="index"
                          >
                            <td>
                              <div>
                                <strong>
                                  {{ record.aliasId }}
                                </strong>
                                <label>
                                  ({{ record.sourceCode }})
                                </label>
                              </div>
                              <div>
                                Person Type: {{ mapPersonType(record.personType) }}
                              </div>
                              <div>
                                Status:  {{ record.active === true ? 'Active' : 'Inactive' }}
                              </div>
                            </td>
                            <td
                              style="width: 30px"
                              :class="{ 'border--left': !splitConfirmed }"
                            >
                              <div
                                v-if="!splitConfirmed"
                                style="text-align: center"
                              >
                              <button
                                  :disabled="!locationAccessPermission"
                                  :class="{
                                    'disabled': !locationAccessPermission
                                  }"
                                  class="button button-link has-underline"
                                  @click="moveAliasIdToRetainedId(record)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'arrow-left-long-to-line']"
                                    title="Move to Retained ID Alias IDs"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  <tr
                    v-if="isPersonInformationHeaderEnabled"
                    style="border-bottom: 1px solid #DEE2E6"
                  >
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Person Information
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="personInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="personInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showPersonInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="togglePersonInformation()"
                          title="Hide Details"
                          @keypress.enter="togglePersonInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="togglePersonInformation()"
                          title="View Details"
                          @keypress.enter="togglePersonInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('ETHNICITY_INDICATOR')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ETHNICITY_INDICATOR') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.ethnicityIndicator, newIdToCreate.ethnicityIndicator
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.ethnicityIndicator, activeEthnicities) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.ethnicityIndicator, newIdToCreate.ethnicityIndicator
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.ethnicityIndicator, activeEthnicities) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('RACE_ETHNIC_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RACE_ETHNIC_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.raceEthnicCode, newIdToCreate.raceEthnicCode,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.raceEthnicCode, activeRaces) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.raceEthnicCode, newIdToCreate.raceEthnicCode,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.raceEthnicCode, activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('RACE2_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RACE2_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race2Code, newIdToCreate.race2Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.race2Code, activeRaces) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race2Code, newIdToCreate.race2Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.race2Code, activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('RACE3_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RACE3_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race3Code, newIdToCreate.race3Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.race3Code, activeRaces) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race3Code, newIdToCreate.race3Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.race3Code, activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('RACE4_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RACE4_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race4Code, newIdToCreate.race4Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.race4Code, activeRaces) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race4Code, newIdToCreate.race4Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.race4Code, activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('RACE5_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RACE5_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race5Code, newIdToCreate.race5Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.race5Code, activeRaces) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.race5Code, newIdToCreate.race5Code)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.race5Code, activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('ETHNIC_SUB_GROUP')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ETHNIC_SUB_GROUP') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.ethnicSubGroup, newIdToCreate.ethnicSubGroup,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.ethnicSubGroup, activeEthnicSubGroups) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.ethnicSubGroup, newIdToCreate.ethnicSubGroup,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.ethnicSubGroup, activeEthnicSubGroups) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('FULL_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('FULL_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.fullName, newIdToCreate.fullName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.fullName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.fullName, newIdToCreate.fullName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.fullName }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showPersonInformation
                      && fieldEnabled('AKA_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('AKA_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.akaName, newIdToCreate.akaName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.akaName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.akaName, newIdToCreate.akaName)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.akaName }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="isContactInformationHeaderEnabled"
                    style="border-bottom: 1px solid #DEE2E6"
                  >
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Contact Information
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="contactInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="contactInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showContactInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleContactInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleContactInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleContactInformation()"
                          title="View Details"
                          @keypress.enter="toggleContactInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('STREET_1')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('STREET_1') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.street1, newIdToCreate.street1)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.street1 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.street1, newIdToCreate.street1)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.street1 }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('STREET_2')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('STREET_2') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.street2, newIdToCreate.street2)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.street2 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.street2, newIdToCreate.street2)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.street2 }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('CITY')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('CITY') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.city, newIdToCreate.city)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.city }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.city, newIdToCreate.city)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.city }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('STATE_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('STATE_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.stateCode, newIdToCreate.stateCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.stateCode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.stateCode, newIdToCreate.stateCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.stateCode }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('ZIP')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ZIP') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.zip, newIdToCreate.zip)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.zip }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.zip, newIdToCreate.zip)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.zip }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showContactInformation
                      && fieldEnabled('GEOCODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('GEOCODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.geocode, newIdToCreate.geocode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.geocode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.geocode, newIdToCreate.geocode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.geocode }}
                      </div>
                    </td>
                  </tr>
                  <tr style="border-bottom: 1px solid #DEE2E6"
                    v-if="personToSplit.studentCdf">
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Customer Defined Fields
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="cdfInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="cdfInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showCdfInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleCdfInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleCdfInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleCdfInformation()"
                          title="View Details"
                          @keypress.enter="toggleCdfInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_1') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_1') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_1, newIdToCreate.studentCdf.cdf_1,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_1 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_1, newIdToCreate.studentCdf.cdf_1,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_1 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_2') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_2') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_2, newIdToCreate.studentCdf.cdf_2,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_2 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_2, newIdToCreate.studentCdf.cdf_2,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_2 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_3') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_3') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_3, newIdToCreate.studentCdf.cdf_3,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_3 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_3, newIdToCreate.studentCdf.cdf_3,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_3 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_4') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_4') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_4, newIdToCreate.studentCdf.cdf_4,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_4 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_4, newIdToCreate.studentCdf.cdf_4,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_4 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_5') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_5') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_5, newIdToCreate.studentCdf.cdf_5,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_5 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_5, newIdToCreate.studentCdf.cdf_5,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_5 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_6') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_6') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_6, newIdToCreate.studentCdf.cdf_6,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_6 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_6, newIdToCreate.studentCdf.cdf_6,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_6 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_7') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_7') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_7, newIdToCreate.studentCdf.cdf_7,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_7 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_7, newIdToCreate.studentCdf.cdf_7,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_7 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_8') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_8') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_8, newIdToCreate.studentCdf.cdf_8,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_8 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_8, newIdToCreate.studentCdf.cdf_8,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_8 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_9') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_9') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_9, newIdToCreate.studentCdf.cdf_9,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_9 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_9, newIdToCreate.studentCdf.cdf_9,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_9 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_10') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_10') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_10, newIdToCreate.studentCdf.cdf_10,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_10 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_10, newIdToCreate.studentCdf.cdf_10,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_10 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('cdf_11') && personToSplit.studentCdf"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('cdf_11') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_11, newIdToCreate.studentCdf.cdf_11,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.studentCdf.cdf_11 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                              personToSplit.studentCdf.cdf_11, newIdToCreate.studentCdf.cdf_11,
                            )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate && newIdToCreate.studentCdf
                            ? newIdToCreate.studentCdf.cdf_11 : undefined
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr style="border-bottom: 1px solid #DEE2E6">
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Location Information
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="locationInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="locationInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showLocationInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleLocationInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleLocationInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleLocationInformation()"
                          title="View Details"
                          @keypress.enter="toggleLocationInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showLocationInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Person Type
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.personType, newIdToCreate.personType)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapPersonType(personToSplit.personType) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.personType, newIdToCreate.personType)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapPersonType(newIdToCreate.personType) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('SCHOOL_YEAR')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SCHOOL_YEAR') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.schoolYear, newIdToCreate.schoolYear)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.schoolYear }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.schoolYear, newIdToCreate.schoolYear)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.schoolYear }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('DISTRICT_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('DISTRICT_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.districtCode, newIdToCreate.districtCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.districtCode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.districtCode, newIdToCreate.districtCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.districtCode }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('SCHOOL_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SCHOOL_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.schoolCode, newIdToCreate.schoolCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.schoolCode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.schoolCode, newIdToCreate.schoolCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.schoolCode }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('RES_DISTRICT_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RES_DISTRICT_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.resDistrictCode, newIdToCreate.resDistrictCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.resDistrictCode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.resDistrictCode, newIdToCreate.resDistrictCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.resDistrictCode }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('LOCAL_STUDENT_ID')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('LOCAL_STUDENT_ID') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.localId, newIdToCreate.localId)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.localId }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.localId, newIdToCreate.localId)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.localId }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('SOURCE_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SOURCE_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.sourceCode, newIdToCreate.sourceCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.sourceCode, activeSources) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.sourceCode, newIdToCreate.sourceCode)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.sourceCode, activeSources) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('ALT_STUDENT_ID')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ALT_STUDENT_ID') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.altStudentId, newIdToCreate.altPersonId)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.altStudentId }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.altStudentId, newIdToCreate.altPersonId)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          newIdToCreate.altPersonId
                            ? newIdToCreate.altPersonId
                            : newIdToCreate.altStudentId
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('ALT_SOURCE_SIS_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ALT_SOURCE_SIS_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.altSourceSisCode, newIdToCreate.altSourceSisCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.altSourceSisCode, activeSources) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.altSourceSisCode, newIdToCreate.altSourceSisCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.altSourceSisCode, activeSources) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('ACTIVE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ACTIVE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.active, newIdToCreate.active)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.active === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.active, newIdToCreate.active)
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreate.active === true ? 'Yes' : 'No' }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('SOURCE_REFRESH_DATE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SOURCE_REFRESH_DATE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            datePortion(personToSplit.sourceRefreshDate),
                            datePortion(newIdToCreate.sourceRefreshDate)
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.sourceRefreshDate | formatDatePreserveZone }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            datePortion(personToSplit.sourceRefreshDate),
                            datePortion(newIdToCreate.sourceRefreshDate)
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreate.sourceRefreshDate | formatDatePreserveZone }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('LOCATION_COMMENT')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('LOCATION_COMMENT') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.locationComment,
                            newIdToCreateLocInfo.locationComment,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.locationComment }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.locationComment,
                            newIdToCreateLocInfo.locationComment,
                          ) : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.locationComment }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('GRADE_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('GRADE_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.gradeCode, newIdToCreateLocInfo.gradeCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplitLocInfo.gradeCode, activeGrades) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.gradeCode, newIdToCreateLocInfo.gradeCode
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ mapLookUps(newIdToCreateLocInfo.gradeCode, activeGrades) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('HIRE_DATE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('HIRE_DATE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.hireDate, newIdToCreateLocInfo.hireDate
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.hireDate | formatDate }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.hireDate, newIdToCreateLocInfo.hireDate
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.hireDate | formatDate }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('SALARY')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SALARY') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.salary, newIdToCreateLocInfo.salary
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.salary }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.salary, newIdToCreateLocInfo.salary
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.salary }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('JOB_CLASS_CODE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('JOB_CLASS_CODE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobClassCode, newIdToCreateLocInfo.jobClassCode
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.jobClassCode }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobClassCode, newIdToCreateLocInfo.jobClassCode
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.jobClassCode }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('JOB_ASSIGNMENT_1')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('JOB_ASSIGNMENT_1') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment1, newIdToCreateLocInfo.jobAssignment1
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.jobAssignment1 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment1, newIdToCreateLocInfo.jobAssignment1
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.jobAssignment1 }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('JOB_ASSIGNMENT_2')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('JOB_ASSIGNMENT_2') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment2, newIdToCreateLocInfo.jobAssignment2
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.jobAssignment2 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment2, newIdToCreateLocInfo.jobAssignment2
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.jobAssignment2 }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('JOB_ASSIGNMENT_3')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('JOB_ASSIGNMENT_3') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment3, newIdToCreateLocInfo.jobAssignment3
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.jobAssignment3 }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.jobAssignment3, newIdToCreateLocInfo.jobAssignment3
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.jobAssignment3 }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showLocationInformation
                      && fieldEnabled('ITINERANT_FLAG')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('ITINERANT_FLAG') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.itinerantFlag, newIdToCreateLocInfo.itinerantFlag
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplitLocInfo.itinerantFlag }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplitLocInfo.itinerantFlag, newIdToCreateLocInfo.itinerantFlag
                          )
                          : false,
                      }"
                    >
                      <div
                        v-if="newIdToCreateIsNotEmpty"
                        class="cell-content"
                      >
                        {{ newIdToCreateLocInfo.itinerantFlag }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="isCredentialInformationHeaderEnabled"
                    style="border-bottom: 1px solid #DEE2E6"
                  >
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Credential Information
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="credentialInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="credentialInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showCredentialInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleCredentialInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleCredentialInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleCredentialInformation()"
                          title="View Details"
                          @keypress.enter="toggleCredentialInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showCredentialInformation
                      && fieldEnabled('HIGHEST_DEGREE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('HIGHEST_DEGREE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.highestDegree, newIdToCreate.highestDegree)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.highestDegree, activeHighestDegrees) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.highestDegree, newIdToCreate.highestDegree)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.highestDegree, activeHighestDegrees) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showCredentialInformation
                      && fieldEnabled('SUBJECT_MATTER_AREA')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('SUBJECT_MATTER_AREA') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.subjectMatterArea, newIdToCreate.subjectMatterArea
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.subjectMatterArea, activeSubjectMatterAreas) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.subjectMatterArea, newIdToCreate.subjectMatterArea
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.subjectMatterArea, activeSubjectMatterAreas) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showCredentialInformation
                      && fieldEnabled('DEGREE_PROVIDER')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('DEGREE_PROVIDER') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.degreeProvider, newIdToCreate.degreeProvider,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.degreeProvider }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.degreeProvider, newIdToCreate.degreeProvider,
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.degreeProvider }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showCredentialInformation
                      && fieldEnabled('CERTIFICATE_NUMBER')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('CERTIFICATE_NUMBER') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.certificateNumber, newIdToCreate.certificateNumber
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.certificateNumber }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.certificateNumber, newIdToCreate.certificateNumber
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.certificateNumber }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showCredentialInformation
                      && fieldEnabled('CERTIFICATE_TYPE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('CERTIFICATE_TYPE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.certificateType, newIdToCreate.certificateType
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.certificateType, activeCertificateTypes) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.certificateType, newIdToCreate.certificateType
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.certificateType, activeCertificateTypes) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="isOtherInformationHeaderEnabled"
                    style="border-bottom: 1px solid #DEE2E6"
                  >
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Other Information
                    </td>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div>
                        <font-awesome-icon
                          v-if="otherInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color right-column-icons">
                      <div></div>
                      <div>
                        <font-awesome-icon
                          v-if="otherInfoDifferent"
                          :icon="['fas', 'exclamation-circle']"
                          size="lg"
                          style="color: #D13241"
                        />
                      </div>
                      <div>
                        <font-awesome-icon
                          v-if="showOtherInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleOtherInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleOtherInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleOtherInformation()"
                          title="View Details"
                          @keypress.enter="toggleOtherInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showOtherInformation
                      && fieldEnabled('BIRTH_LOCATION')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('BIRTH_LOCATION') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.birthLocation, newIdToCreate.birthLocation)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.birthLocation }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(personToSplit.birthLocation, newIdToCreate.birthLocation)
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.birthLocation }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showOtherInformation
                      && fieldEnabled('RESIDENCY_STATUS')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RESIDENCY_STATUS') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.residencyStatus, newIdToCreate.residencyStatus
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(personToSplit.residencyStatus, activeResidencyStatuses) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.residencyStatus, newIdToCreate.residencyStatus
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ mapLookUps(newIdToCreate.residencyStatus, activeResidencyStatuses) }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showOtherInformation
                      && fieldEnabled('RESIDENCY_EXPIRATION_DATE')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('RESIDENCY_EXPIRATION_DATE') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.residencyExpirationDate,
                            newIdToCreate.residencyExpirationDate
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.residencyExpirationDate | formatDate }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.residencyExpirationDate,
                            newIdToCreate.residencyExpirationDate
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.residencyExpirationDate | formatDate }}
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="showOtherInformation
                      && fieldEnabled('MOTHERS_MAIDEN_NAME')"
                  >
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        {{ fieldLabel('MOTHERS_MAIDEN_NAME') }}
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.mothersMaidenName, newIdToCreate.mothersMaidenName
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ personToSplit.mothersMaidenName }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': newIdToCreateIsNotEmpty
                          ? compareFields(
                            personToSplit.mothersMaidenName, newIdToCreate.mothersMaidenName
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{ newIdToCreate.mothersMaidenName }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Transaction Information
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div class="align--end">
                        <font-awesome-icon
                          v-if="showTransactionInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleTransactionInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleTransactionInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleTransactionInformation()"
                          title="View Details"
                          @keypress.enter="toggleTransactionInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showTransactionInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Transaction Type
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': (
                          Object.keys(newIdToCreateTransactionInfo).length !== 0 &&
                          personToSplitTransactionInfo &&
                          personToSplitTransactionInfo.pidBatch &&
                          newIdToCreateTransactionInfo.pidBatch
                        ) ? compareFields(
                            personToSplitTransactionInfo.pidBatch.submissionTypeDescription,
                            newIdToCreateTransactionInfo.pidBatch.submissionTypeDescription
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          personToSplitTransactionInfo && personToSplitTransactionInfo.pidBatch
                            ? personToSplitTransactionInfo.pidBatch.submissionTypeDescription
                            : null
                        }}
                      </div>
                    </td>
                    <td class="blank-column border--left" />
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': (
                          Object.keys(newIdToCreateTransactionInfo).length !== 0 &&
                          personToSplitTransactionInfo &&
                          personToSplitTransactionInfo.pidBatch &&
                          newIdToCreateTransactionInfo.pidBatch
                        ) ? compareFields(
                            personToSplitTransactionInfo.pidBatch.submissionTypeDescription,
                            newIdToCreateTransactionInfo.pidBatch.submissionTypeDescription
                          ) : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          Object.keys(newIdToCreateTransactionInfo).length !== 0 &&
                          newIdToCreateTransactionInfo.pidBatch
                            ? newIdToCreateTransactionInfo.pidBatch.submissionTypeDescription
                            : null
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showTransactionInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Last Transaction ID
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': Object.keys(newIdToCreateTransactionInfo).length !== 0
                          ? compareFields(
                            personToSplitTransactionInfo.pidSubmissionId,
                            newIdToCreateTransactionInfo.pidSubmissionId,
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          personToSplitTransactionInfo
                            ? personToSplitTransactionInfo.pidSubmissionId : null
                        }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': Object.keys(newIdToCreateTransactionInfo).length !== 0
                        ? compareFields(
                            personToSplitTransactionInfo.pidSubmissionId,
                            newIdToCreateTransactionInfo.pidSubmissionId,
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          Object.keys(newIdToCreateTransactionInfo).length !== 0
                            ? (newIdToCreateTransactionInfo
                                ? newIdToCreateTransactionInfo.pidSubmissionId : null)
                            : null
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showTransactionInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Last Batch ID
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': Object.keys(newIdToCreateTransactionInfo).length !== 0
                          ? compareFields(
                            personToSplitTransactionInfo.pidBatchId,
                            newIdToCreateTransactionInfo.pidBatchId,
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          personToSplitTransactionInfo && personToSplitTransactionInfo.pidBatchId
                            ? personToSplitTransactionInfo.pidBatchId : null
                        }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': Object.keys(newIdToCreateTransactionInfo).length !== 0
                        ? compareFields(
                            personToSplitTransactionInfo.pidBatchId,
                            newIdToCreateTransactionInfo.pidBatchId,
                          )
                          : false,
                      }"
                    >
                      <div class="cell-content">
                        {{
                          Object.keys(newIdToCreateTransactionInfo).length !== 0
                            ? newIdToCreateTransactionInfo.pidBatchId : null
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel-heading-bg-color has-text-weight-bold">
                      Matching Information
                    </td>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color"/>
                    <td class="compare-panel-heading-bg-color text-align--center">
                      <div class="align--end">
                        <font-awesome-icon
                          v-if="showMatchingInformation"
                          :icon="['far', 'caret-up']"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleMatchingInformation()"
                          title="Hide Details"
                          @keypress.enter="toggleMatchingInformation()"
                          tabindex="0"
                          role="button"
                        />
                        <font-awesome-icon
                          v-else
                          :icon="['far', 'caret-down']"
                          size="lg"
                          style="cursor: pointer; font-size: 1.25rem; color: #5953A4"
                          @click="toggleMatchingInformation()"
                          title="View Details"
                          @keypress.enter="toggleMatchingInformation()"
                          tabindex="0"
                          role="button"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showMatchingInformation && !splitConfirmed">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Match Decision
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{ 'panel--highlighted': splitPendingMatchDecisionDifferent }"
                    >
                      <div class="cell-content">
                        {{ mapMatchDecisionInfo(personToSplitMatchingInfo) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{ 'panel--highlighted': splitPendingMatchDecisionDifferent }"
                    >
                      <div class="cell-content">
                        <span >
                          {{ mapMatchDecisionInfo(newIdToCreateMatchingInfo) }}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showMatchingInformation && splitConfirmed">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Match Decision
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{ 'panel--highlighted': splitConfirmedMatchDecisionDifferent }"
                    >
                      <div class="cell-content">
                        {{ mapMatchDecisionInfo(personToSplitMatchingInfo) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{ 'panel--highlighted': splitConfirmedMatchDecisionDifferent }"
                    >
                      <div class="cell-content">
                        <span >
                          {{
                            Object.keys(newIdToCreateMatchingInfo).length !== 0
                              && newIdToCreateMatchingInfo
                              && newIdToCreateMatchingInfo.pidMatchTransId
                              ? matchDecisionInfo(
                                newIdToCreateMatchingInfo.pidMatchTransId.note
                              ) : null
                          }}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="showMatchingInformation">
                    <td class="compare-panel-field-cell">
                      <div class="submission-header has-text-left">
                        Record Status
                      </div>
                    </td>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                      :class="{ 'panel--highlighted': recordStatusIsDifferent }"
                    >
                      <div class="cell-content">
                        {{ getMatchingInfoStatus(this.personToSplitMatchingInfo) }}
                      </div>
                    </td>
                    <td class="blank-column border--left"/>
                    <td
                      class="compare-panel-field-cell border--left vertical-align--bottom"
                       :class="{ 'panel--highlighted': recordStatusIsDifferent }"
                    >
                      <div class="cell-content">
                        {{ getMatchingInfoStatus(this.newIdToCreateMatchingInfo) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          v-if="!splitConfirmed"
          class="is-flex flex-justify--end flex-align--center user-actions"
          id="bottom-actions"
        >
          <div>
            <button
              class="button button-link has-underline"
              @click="reset"
              style="width: 60px"
              :disabled="!newIdToCreateIsNotEmpty"
            >
              Reset
            </button>
            <button
              class="button is-primary"
              style="width: 100px"
              @click="submit()"
              :disabled="!newIdToCreateIsNotEmpty"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import {
  faCaretDown,
  faCaretUp,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeftLongToLine,
  faArrowRightLongToLine,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import StateIdUtils from '@/mixins/StateIdUtils';

library.add(
  faArrowLeftLongToLine,
  faArrowRightLongToLine,
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
  faPlus,
);

function myFunction() {
  const footer = document.getElementById('bottom-actions');
  if (footer !== null) {
    footer.classList.add('static-bottom');
  }
}

window.onscroll = myFunction();

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
    StateIdUtils,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    allHistMatchingInfo: {
      type: Array,
    },
    allHistTransactionInfo: {
      type: Array,
    },
    cannotSplit: {
      type: Boolean,
    },
    dataAccessErrorMessage: {
      type: String,
    },
    dataAccessPermission: {
      type: Boolean,
    },
    dataAccessPermissionNearMatch: {
      type: Boolean,
    },
    originalPersonToSplit: {
      type: Object,
    },
    originalPersonToSplitAliasIdInfo: {
      type: Array,
    },
    originalPersonToSplitHistInfo: {
      type: Array,
    },
    originalPersonToSplitLocInfo: {
      type: Object,
    },
    originalPersonToSplitLocHistInfo: {
      type: Array,
    },
    originalPersonToSplitMatchingInfo: {
      type: Object,
    },
    originalPersonToSplitTransactionInfo: {
      type: Object,
    },
    newPerson: {
      type: Object,
      default: null,
    },
    newPersonAliasIdInfo: {
      type: Array,
      default: null,
    },
    newPersonHistInfo: {
      type: Array,
      default: null,
    },
    newPersonLocInfo: {
      type: Object,
      default: null,
    },
    newPersonLocHistInfo: {
      type: Array,
      default: null,
    },
    newPersonMatchingInfo: {
      type: Object,
    },
    newPersonTransactionInfo: {
      type: Object,
    },
    personTypeMisMatched: {
      type: Boolean,
    },
    splitConfirmed: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  async created() {
    this.personToSplit = JSON.parse(JSON.stringify(this.originalPersonToSplit));
    this.personToSplitAliasIdInfo = JSON.parse(
      JSON.stringify(this.originalPersonToSplitAliasIdInfo),
    );
    this.personToSplitHistInfo = JSON.parse(JSON.stringify(this.originalPersonToSplitHistInfo));
    this.personToSplitLocInfo = JSON.parse(JSON.stringify(this.originalPersonToSplitLocInfo));
    this.personToSplitLocHistInfo = JSON.parse(
      JSON.stringify(this.originalPersonToSplitLocHistInfo),
    );
    this.personToSplitMatchingInfo = JSON.parse(
      JSON.stringify(this.originalPersonToSplitMatchingInfo),
    );
    this.personToSplitTransactionInfo = JSON.parse(
      JSON.stringify(this.originalPersonToSplitTransactionInfo),
    );
    if (this.personToSplit && this.personToSplit.stateStudentId) {
      this.idToRetain = this.personToSplit.stateStudentId;
    }
    if (this.newPerson !== null) {
      this.newIdToCreate = JSON.parse(JSON.stringify(this.newPerson));
    }
    if (this.newPersonAliasIdInfo !== null) {
      this.newIdToCreateAliasIdInfo = JSON.parse(
        JSON.stringify(this.newPersonAliasIdInfo),
      );
    }
    if (this.newPersonHistInfo !== null) {
      this.newIdToCreateHistInfo = JSON.parse(JSON.stringify(this.newPersonHistInfo));
    }
    if (this.newPersonLocInfo !== null) {
      this.newIdToCreateLocInfo = JSON.parse(JSON.stringify(this.newPersonLocInfo));
    }
    if (this.newPersonLocHistInfo !== null) {
      this.newIdToCreateLocHistInfo = JSON.parse(
        JSON.stringify(this.newPersonLocHistInfo),
      );
    }
    if (this.newPersonMatchingInfo !== null) {
      this.newIdToCreateMatchingInfo = JSON.parse(
        JSON.stringify(this.newPersonMatchingInfo),
      );
    }
    if (this.newPersonTransactionInfo !== null) {
      this.newIdToCreateTransactionInfo = JSON.parse(
        JSON.stringify(this.newPersonTransactionInfo),
      );
    }
    this.onLoad();
  },
  mounted() {
    this.rowSpanCountNum = this.rowSpanCount();
    this.enableSectionHeaders();
  },
  computed: {
    ...mapGetters('lookups', [
      'activeCertificateTypes',
      'activeEthnicities',
      'activeEthnicSubGroups',
      'activeGenders',
      'activeGrades',
      'activeHighestDegrees',
      'activeJobAssignments',
      'activePersonTypes',
      'activeRaces',
      'activeResidencyStatuses',
      'activeSources',
      'activeStates',
      'activeSubjectMatterAreas',
      'activeTitles',
      'defaultPersonType',
      'pidTransactionStatuses',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldEnabledForPersonType',
    ]),
    cdfInfoDifferent() {
      return this.isCdfInfoDifferent([
        'cdf_1',
        'cdf_2',
        'cdf_3',
        'cdf_4',
        'cdf_5',
        'cdf_6',
        'cdf_7',
        'cdf_8',
        'cdf_9',
        'cdf_10',
        'cdf_11',
      ]);
    },
    contactInfoDifferent() {
      return this.isInfoDifferent([
        'street1',
        'street2',
        'city',
        'stateCode',
        'zip',
        'geocode',
      ]);
    },
    credentialInfoDifferent() {
      return this.isInfoDifferent([
        'highestDegree',
        'subjectMatterArea',
        'degreeProvider',
        'certificateNumber',
        'certificateType',
      ]);
    },
    displayData() {
      const temp = {};
      temp.retainId = this.personToSplit;
      temp.retainIdLocInfo = this.personToSplitLocInfo;
      temp.retainIdHistCount = this.personToSplitHistInfo
        ? this.personToSplitHistInfo.length : null;
      temp.newId = this.newIdToCreate;
      temp.newIdLocInfo = this.newIdToCreateLocInfo;
      temp.newIdHistCount = this.newIdToCreateHistInfo
        ? this.newIdToCreateHistInfo.length : null;
      return temp;
    },
    fieldsForRowSpan() {
      return [
        'FIRST_NAME',
        'MIDDLE_NAME',
        'LAST_NAME',
        'SUFFIX',
        'PREVIOUS_LAST_NAME',
        'GENDER_CODE',
        'DATE_OF_BIRTH',
      ];
    },
    groups() {
      const temp = [];
      temp.push(this.retainId);
      temp.push(this.newId);
      return temp;
    },
    locationInfoDifferent() {
      return this.isInfoDifferent([
        'personType',
        'schoolYear',
        'districtCode',
        'schoolCode',
        'resDistrictCode',
        'localId',
        'sourceCode',
        'altSourceSisCode',
        'active',
        'sourceRefreshDate',
        'locationComment',
        'gradeCode',
        'hireDate',
        'salary',
        'jobClassCode',
        'jobAssignment1',
        'jobAssignment2',
        'jobAssignment3',
        'itinerantFlag',
      ]) || (
        this.newIdToCreateIsNotEmpty && this.compareFields(
          this.personToSplit.altStudentId, this.newIdToCreate.altPersonId,
        )
      );
    },
    locationAccessPermission() {
      const hasUserDistrictAccess = this.setOfUserDistricts.length === 0
        || this.setOfPersonDistricts.every(district => this.setOfUserDistricts.includes(district));
      const hasUserSchoolAccess = this.setOfUserSchools.length === 0
        || this.setOfPersonSchools.every(school => this.setOfUserSchools.includes(school));
      const hasUserPersonTypeAccess = this.setOfUserPersonTypes.length === 0
        || this.setOfPersonPersonTypes
          .every(personType => this.setOfUserPersonTypes.includes(personType));

      if (hasUserDistrictAccess && hasUserSchoolAccess && hasUserPersonTypeAccess) {
        return true;
      }
      return false;
    },
    matchingFieldsInfoDifferent() {
      return this.isInfoDifferent([
        'firstName',
        'middleName',
        'lastName',
        'suffix',
        'previousLastName',
        'genderCode',
        'dateOfBirth',
        'socialSecurityNumber',
      ]);
    },
    mapMatchDecisionInfo() {
      return matchingInfo => (
        (matchingInfo
          && matchingInfo.pidMatchTransDetail
          && matchingInfo.pidMatchTransDetail.response)
          ? this.matchDecisionInfo(matchingInfo.pidMatchTransDetail.response.matchDecision)
          : null
      );
    },
    newId() {
      const temp = {};
      temp.command = 'SPLIT_GET_NEW_ID';
      temp.personId = this.idToRetain;
      if (this.newIdToCreate && this.newIdToCreate.lastTranSerialNumber) {
        temp.lastTranSerialNumber = this.newIdToCreate.lastTranSerialNumber;
      }
      if (this.newIdToCreateHistInfo.length) {
        temp.historyList = [];
        this.newIdToCreateHistInfo.forEach((e) => {
          temp.historyList.push(
            {
              personId: e.personId,
              lastTranSerialNumber: e.lastTranSerialNumber,
            },
          );
        });
      }
      if (this.newIdToCreateAliasIdInfo.length) {
        temp.aliasList = [];
        this.newIdToCreateAliasIdInfo.forEach((e) => {
          temp.aliasList.push(
            {
              id: e.id,
              aliasId: e.aliasId,
              tranSerialNumber: e.tranSerialNumber,
            },
          );
        });
      }
      return temp;
    },
    newIdToCreateIsNotEmpty() {
      if (Object.keys(this.newIdToCreate).length !== 0) {
        return true;
      } return false;
    },
    otherInfoDifferent() {
      return this.isInfoDifferent([
        'birthLocation',
        'residencyStatus',
        'residencyExpirationDate',
        'certificateNumber',
        'mothersMaidenName',
      ]);
    },
    personInfoDifferent() {
      return this.isInfoDifferent([
        'ethnicityIndicator',
        'raceEthnicCode',
        'race2Code',
        'race3Code',
        'race4Code',
        'race5Code',
        'ethnicSubGroup',
        'fullName',
        'akaName',
      ]);
    },
    personTypeAccess() {
      return this.setOfPersonPersonTypes
        .every(personType => this.setOfUserPersonTypes.includes(personType));
    },
    personTypeAccessErrorMessage() {
      return `User is not authorized to submit records to the specified person type:Person Type=
        ${this.setOfPersonPersonTypes.filter(personType => !this.setOfUserPersonTypes.includes(personType))}`;
    },
    prepForRequest() {
      const payload = {};
      payload.idToSplit = this.idToRetain;
      payload.groups = this.groups;
      return payload;
    },
    recordStatusIsDifferent() {
      return Object.keys(this.newIdToCreateMatchingInfo).length !== 0
        ? this.compareFields(
          this.personToSplitMatchingInfo.transStatus,
          this.newIdToCreateMatchingInfo.transStatus,
        )
        : false;
    },
    retainId() {
      if (this.personToSplit && this.personToSplit.lastTranSerialNumber) {
        const temp = {};
        temp.command = 'SPLIT_KEEP_ORIGINAL_ID';
        temp.personId = this.idToRetain;
        temp.lastTranSerialNumber = this.personToSplit.lastTranSerialNumber;
        if (this.personToSplitHistInfo.length) {
          temp.historyList = [];
          this.personToSplitHistInfo.forEach((e) => {
            temp.historyList.push(
              {
                personId: e.personId,
                lastTranSerialNumber: e.lastTranSerialNumber,
              },
            );
          });
        }
        if (this.personToSplitAliasIdInfo.length) {
          temp.aliasList = [];
          this.personToSplitAliasIdInfo.forEach((e) => {
            temp.aliasList.push(
              {
                id: e.id,
                aliasId: e.aliasId,
                tranSerialNumber: e.tranSerialNumber,
              },
            );
          });
        }
        return temp;
      } return null;
    },
    setOfPersonDistricts() {
      if (this.personToSplit && this.personToSplit.districtCode) {
        const uniqueDistrictCodes = new Set();
        uniqueDistrictCodes.add(this.personToSplit.districtCode);
        this.personToSplitHistInfo.forEach((info) => {
          uniqueDistrictCodes.add(info.districtCode);
        });
        return [...uniqueDistrictCodes];
      }
      return [];
    },
    setOfPersonPersonTypes() {
      if (this.personToSplit && this.personToSplit.personType) {
        const uniquePersonTypes = new Set();
        uniquePersonTypes.add(this.personToSplit.personType);
        this.personToSplitHistInfo.forEach((info) => {
          uniquePersonTypes.add(info.personType);
        });
        return [...uniquePersonTypes];
      }
      return [];
    },
    setOfPersonSchools() {
      if (this.personToSplit && this.personToSplit.schoolCode) {
        const uniqueSchoolCodes = new Set();
        uniqueSchoolCodes.add(this.personToSplit.schoolCode);
        this.personToSplitHistInfo.forEach((info) => {
          uniqueSchoolCodes.add(info.schoolCode);
        });
        return [...uniqueSchoolCodes];
      }
      return [];
    },
    setOfUserDistricts() {
      return this.$store.state.user.locationPermissions[0].districts.map(a => a.code);
    },
    setOfUserPersonTypes() {
      return this.$store.state.user.userPersonTypes.map(a => a.personTypeCode);
    },
    setOfUserSchools() {
      return this.$store.state.user.locationPermissions[0].schools.map(a => a.code);
    },
    splitConfirmedMatchDecisionDifferent() {
      return Object.keys(this.newIdToCreateMatchingInfo).length !== 0
        ? this.compareFields(
          this.personToSplitMatchingInfo
          && this.personToSplitMatchingInfo.pidMatchTransDetail
          && this.personToSplitMatchingInfo.pidMatchTransDetail.response
            ? this.matchDecisionInfo(
              this.personToSplitMatchingInfo.pidMatchTransDetail.response.matchDecision,
            ) : null,
          Object.keys(this.newIdToCreateMatchingInfo).length !== 0
              && this.newIdToCreateMatchingInfo
              && this.newIdToCreateMatchingInfo.pidMatchTransId
              && this.newIdToCreateMatchingInfo.pidMatchTransId
            ? this.matchDecisionInfo(
              this.newIdToCreateMatchingInfo.pidMatchTransId.note,
            ) : null,
        ) : false;
    },
    splitPendingMatchDecisionDifferent() {
      return Object.keys(this.newIdToCreateMatchingInfo).length !== 0
        ? this.compareFields(
          this.personToSplitMatchingInfo
          && this.personToSplitMatchingInfo.pidMatchTransDetail
          && this.personToSplitMatchingInfo.pidMatchTransDetail.response
            ? this.matchDecisionInfo(
              this.personToSplitMatchingInfo.pidMatchTransDetail.response.matchDecision,
            ) : null,
          Object.keys(this.newIdToCreateMatchingInfo).length !== 0
              && this.newIdToCreateMatchingInfo
              && this.newIdToCreateMatchingInfo.pidMatchTransDetail
              && this.newIdToCreateMatchingInfo.pidMatchTransDetail.response
            ? this.matchDecisionInfo(
              this.newIdToCreateMatchingInfo.pidMatchTransDetail.response.matchDecision,
            ) : null,
        )
        : false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadCertificateTypes',
      'loadEthnicities',
      'loadEthnicSubGroups',
      'loadGenders',
      'loadGrades',
      'loadHighestDegrees',
      'loadPersonTypes',
      'loadPidTransactionStatuses',
      'loadRaces',
      'loadResidencyStatuses',
      'loadSources',
      'loadSubjectMatterAreas',
    ]),
    compareFields(oldId, newId) {
      if (oldId === newId) {
        return false;
      }
      return true;
    },
    datePortion(value) {
      // Normalize to the date portion only, ignoring any time component, so
      // date-only and datetime values for the same day compare as equal.
      // parseZone keeps the offset written in the value (e.g. "+05:30") rather
      // than converting to local time, so the date is not shifted by timezone.
      // Empty or invalid values normalize to null so two empties compare equal
      // and a garbage value never produces the literal "Invalid date" string.
      if (!value) return null;
      const m = moment.parseZone(value);
      return m.isValid() ? m.format('YYYY-MM-DD') : null;
    },
    createNewMaster(data) {
      const temp = data;
      temp.studentCdf = temp.personCdf;
      delete temp.personCdf;
      this.newIdToCreate = temp;
      this.$emit('flipSummary', this.newIdToCreate);
      // eslint-disable-next-line no-return-assign, no-param-reassign
      this.newIdToCreateLocHistInfo = this.personToSplitLocHistInfo
        .find(a => a.lastTranSerialNumber === data.lastTranSerialNumber);
      this.newIdToCreateLocInfo = this.personToSplitLocHistInfo
        .find(a => a.lastTranSerialNumber === this.newIdToCreateLocHistInfo.lastTranSerialNumber);
      const index2 = this.personToSplitLocHistInfo.indexOf(this.newIdToCreateLocHistInfo);
      this.personToSplitLocHistInfo.splice(index2, 1);
      const index = this.personToSplitHistInfo.indexOf(data);
      this.personToSplitHistInfo.splice(index, 1);
      this.localHistMatchingInfo = this.allHistMatchingInfo.filter(item => item !== undefined);
      if (this.localHistMatchingInfo.length !== 0) {
        this.newIdToCreateMatchingInfo = this.localHistMatchingInfo
          .find(a => a.pidSubmissionId === data.lastTranSerialNumber);
      }
      if (!this.newIdToCreateMatchingInfo) this.newIdToCreateMatchingInfo = {};
      this.localHistTransactionInfo = this.allHistTransactionInfo.filter(item => item !== undefined && item !== '');
      if (this.localHistTransactionInfo.length !== 0) {
        this.newIdToCreateTransactionInfo = this.localHistTransactionInfo
          .find(a => a.pidSubmissionId === data.lastTranSerialNumber);
      }
      if (!this.newIdToCreateTransactionInfo) this.newIdToCreateTransactionInfo = {};
    },
    displayFields(fields) {
      return fields.some(field => this.fieldEnabled(field));
    },
    enableSectionHeaders() {
      const contactInformationFields = [
        'STREET_1',
        'STREET_2',
        'CITY',
        'STATE_CODE',
        'ZIP',
        'GEOCODE',
      ];
      const credentialInformationFields = [
        'HIGHEST_DEGREE',
        'SUBJECT_MATTER_AREA',
        'DEGREE_PROVIDER',
        'CERTIFICATE_NUMBER',
        'CERTIFICATE_TYPE',
      ];
      const matchingFieldsFields = [
        'FIRST_NAME',
        'MIDDLE_NAME',
        'LAST_NAME',
        'SUFFIX',
        'PREVIOUS_LAST_NAME',
        'GENDER_CODE',
        'DATE_OF_BIRTH',
        'SOCIAL_SECURITY_NUMBER',
      ];
      const otherInformationFields = [
        'BIRTH_LOCATION',
        'RESIDENCY_STATUS',
        'RESIDENCY_EXPIRATION_DATE',
        'MOTHERS_MAIDEN_NAME',
      ];
      const personInformationFields = [
        'ETHNICITY_INDICATOR',
        'RACE_ETHNIC_CODE',
        'RACE2_CODE',
        'RACE3_CODE',
        'RACE4_CODE',
        'RACE5_CODE',
        'FULL_NAME',
        'AKA_NAME',
      ];
      this.isContactInformationHeaderEnabled = this.displayFields.call(
        this, contactInformationFields,
      );
      this.isCredentialInformationHeaderEnabled = this.displayFields.call(
        this, credentialInformationFields,
      );
      this.isMatchingFieldsHeaderEnabled = this.displayFields.call(this, matchingFieldsFields);
      this.isOtherInformationHeaderEnabled = this.displayFields.call(this, otherInformationFields);
      this.isPersonInformationHeaderEnabled = this.displayFields.call(
        this, personInformationFields,
      );
    },
    fieldEnabled(code) {
      if (this.personToSplit && this.personToSplit.personType) {
        return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.personToSplit.personType);
      } return null;
    },
    fieldLabel(code) {
      if (this.personToSplit && this.personToSplit.personType) {
        return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personToSplit.personType);
      } return null;
    },
    getMatchingInfoStatus(matchingInfo) {
      return this.getStatusInfo(matchingInfo);
    },
    getStatusInfo(matchingInfo) {
      return (
        matchingInfo
        && matchingInfo.transStatus
        && this.recordStatusInfo(matchingInfo.transStatus)
      );
    },
    initialData() {
      return {
        formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
        hasErrorIdToSplit: false,
        idToRetain: null,
        isContactInformationHeaderEnabled: false,
        isCredentialInformationHeaderEnabled: false,
        isMatchingFieldsHeaderEnabled: false,
        isOtherInformationHeaderEnabled: false,
        isPersonInformationHeaderEnabled: false,
        loading: false,
        loadingError: false,
        localHistMatchingInfo: [],
        localHistTransactionInfo: [],
        msgIdToSplit: null,
        newIdToCreate: {},
        newIdToCreateAliasIdInfo: [],
        newIdToCreateHistInfo: [],
        newIdToCreateLocInfo: {},
        newIdToCreateLocHistInfo: [],
        newIdToCreateMatchingInfo: {},
        newIdToCreateTransactionInfo: {},
        personToSplit: null,
        personToSplitAliasIdInfo: null,
        personToSplitHistInfo: null,
        personToSplitLocInfo: null,
        personToSplitLocHistInfo: null,
        personToSplitMatchingInfo: null,
        personToSplitTransactionInfo: null,
        rowSpanCountNum: 0,
        showAliasIds: true,
        showCdfInformation: true,
        showContactInformation: true,
        showCredentialInformation: true,
        showHistoryInformation: true,
        showLocationInformation: true,
        showMatchingFields: true,
        showMatchingInformation: true,
        showOtherInformation: true,
        showPersonInformation: true,
        showTransactionInformation: true,
        stateId: null,
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),
      };
    },
    isCdfInfoDifferent(fieldsToCompare) {
      if (!this.newIdToCreateIsNotEmpty) return false;
      return fieldsToCompare.some(
        field => this.compareFields(
          this.personToSplit.studentCdf[field],
          this.newIdToCreate.studentCdf[field],
        ),
      );
    },
    isInfoDifferent(fieldsToCompare) {
      if (!this.newIdToCreateIsNotEmpty) return false;
      return fieldsToCompare.some(
        field => this.compareFields(this.personToSplit[field], this.newIdToCreate[field]),
      );
    },
    isWholeNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 83) {
        evt.preventDefault();
      }
    },
    matchDecisionInfo(status) {
      const statusMapping = {
        MATCHED: 'Matched',
        NO_MATCHES: 'No Match',
        NEAR_MATCHED: 'Near Matched',
        'direct edit': 'Direct Edit',
      };
      return statusMapping[status] || null;
    },
    mapPersonType(personTypeCode) {
      const item = this.activePersonTypes.find(pt => pt.code === personTypeCode);
      return item ? item.name : '\u00A0';
    },
    mapLookUps(code, array) {
      const item = array.find(i => i.code === code);
      return item ? item.description : '\u00A0';
    },
    moveAliasIdToNewId(data) {
      this.newIdToCreateAliasIdInfo.push(data);
      const index = this.personToSplitAliasIdInfo.indexOf(data);
      this.personToSplitAliasIdInfo.splice(index, 1);
    },
    moveAliasIdToRetainedId(data) {
      this.personToSplitAliasIdInfo.push(data);
      const index = this.newIdToCreateAliasIdInfo.indexOf(data);
      this.newIdToCreateAliasIdInfo.splice(index, 1);
    },
    moveHistoryToNewId(data) {
      this.newIdToCreateHistInfo.push(data);
      const index = this.personToSplitHistInfo.indexOf(data);
      this.personToSplitHistInfo.splice(index, 1);
    },
    moveHistoryToRetainedId(data) {
      this.personToSplitHistInfo.push(data);
      const index = this.newIdToCreateHistInfo.indexOf(data);
      this.newIdToCreateHistInfo.splice(index, 1);
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadCertificateTypes(),
          this.loadEthnicities(),
          this.loadEthnicSubGroups(),
          this.loadGenders(),
          this.loadGrades(),
          this.loadHighestDegrees(),
          this.loadPersonTypes(),
          this.loadPidTransactionStatuses(),
          this.loadRaces(),
          this.loadResidencyStatuses(),
          this.loadSources(),
          this.loadSubjectMatterAreas(),
        ]);
      } catch (error) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    recordStatusInfo(code) {
      return this.pidTransactionStatuses.map(r => r.list).flat()
        .find(x => x.id === code).description;
    },
    reset() {
      this.personToSplit = JSON.parse(JSON.stringify(this.originalPersonToSplit));
      this.personToSplitAliasIdInfo = JSON.parse(
        JSON.stringify(this.originalPersonToSplitAliasIdInfo),
      );
      this.personToSplitHistInfo = JSON.parse(JSON.stringify(this.originalPersonToSplitHistInfo));
      this.personToSplitLocInfo = JSON.parse(JSON.stringify(this.originalPersonToSplitLocInfo));
      this.personToSplitLocHistInfo = JSON.parse(
        JSON.stringify(this.originalPersonToSplitLocHistInfo),
      );
      this.personToSplitMatchingInfo = JSON.parse(
        JSON.stringify(this.originalPersonToSplitMatchingInfo),
      );
      this.personToSplitTransactionInfo = JSON.parse(
        JSON.stringify(this.originalPersonToSplitTransactionInfo),
      );
      this.newIdToCreate = {};
      this.newIdToCreateAliasIdInfo = [];
      this.newIdToCreateHistInfo = [];
      this.newIdToCreateLocInfo = {};
      this.newIdToCreateLocHistInfo = [];
      this.newIdToCreateMatchingInfo = {};
      this.newIdToCreateTransactionInfo = {};
      this.$emit('flipSummary', this.newIdToCreate);
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    rowSpanCount() {
      let count = this.fieldsForRowSpan.reduce(
        (acc, field) => (this.fieldEnabled(field) ? acc + 1 : acc), 0,
      );
      if (this.fieldEnabled('SOCIAL_SECURITY_NUMBER') && this.hasModule('PRSID:PRPRF:SSN:VIEW')) {
        count += 1;
      }
      return count;
    },
    showError(error) {
      this.showNotification('danger', [error]);
    },
    submit() {
      this.$emit('showReviewSplitIdConfirmation', {
        requestData: this.prepForRequest,
        displayData: this.displayData,
        retainedId: this.idToRetain,
      });
    },
    switchTheData() {
      const temp = this.personToSplit;
      this.personToSplit = this.newIdToCreate;
      // Begin bugfix/NQD-4239
      temp.altPersonId = temp.altStudentId;
      delete temp.altStudentId;
      this.personToSplit.altStudentId = this.personToSplit.altPersonId;
      delete this.personToSplit.altPersonId;
      // End bugfix/NQD-4239
      this.newIdToCreate = temp;
      const temp2 = this.personToSplitAliasIdInfo;
      this.personToSplitAliasIdInfo = this.newIdToCreateAliasIdInfo;
      this.newIdToCreateAliasIdInfo = temp2;
      const temp3 = this.personToSplitHistInfo;
      this.personToSplitHistInfo = this.newIdToCreateHistInfo;
      this.newIdToCreateHistInfo = temp3;
      const temp4 = this.personToSplitLocInfo;
      this.personToSplitLocInfo = this.newIdToCreateLocInfo;
      this.newIdToCreateLocInfo = temp4;
      const temp5 = this.personToSplitLocHistInfo;
      this.personToSplitLocHistInfo = this.newIdToCreateLocHistInfo;
      this.newIdToCreateLocHistInfo = temp5;
      const temp6 = this.personToSplitMatchingInfo;
      this.personToSplitMatchingInfo = this.newIdToCreateMatchingInfo;
      this.newIdToCreateMatchingInfo = temp6;
      if (this.personToSplitTransactionInfo === null) {
        this.personToSplitTransactionInfo = {};
      }
      const temp7 = this.personToSplitTransactionInfo;
      this.personToSplitTransactionInfo = this.newIdToCreateTransactionInfo;
      this.newIdToCreateTransactionInfo = temp7;
    },
    toggleAliasIds() {
      this.showAliasIds = !this.showAliasIds;
    },
    toggleCdfInformation() {
      this.showCdfInformation = !this.showCdfInformation;
    },
    toggleContactInformation() {
      this.showContactInformation = !this.showContactInformation;
    },
    toggleCredentialInformation() {
      this.showCredentialInformation = !this.showCredentialInformation;
    },
    toggleHistoryInformation() {
      this.showHistoryInformation = !this.showHistoryInformation;
    },
    toggleLocationInformation() {
      this.showLocationInformation = !this.showLocationInformation;
    },
    toggleMatchingFields() {
      this.showMatchingFields = !this.showMatchingFields;
    },
    toggleMatchingInformation() {
      this.showMatchingInformation = !this.showMatchingInformation;
    },
    toggleOtherInformation() {
      this.showOtherInformation = !this.showOtherInformation;
    },
    togglePersonInformation() {
      this.showPersonInformation = !this.showPersonInformation;
    },
    toggleTransactionInformation() {
      this.showTransactionInformation = !this.showTransactionInformation;
    },
    validateField() {
      if (!this.stateId) {
        this.hasErrorIdToSplit = true;
        this.msgIdToSplit = 'ID to Split is required.';
      } else {
        this.hasErrorIdToSplit = false;
        this.msgIdToSplit = null;
        this.resetNotifications();
      }
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        const [year, month, day] = value.split('-');
        return `${month}/${day}/${year}`;
      }
      return null;
    },
    formatLUDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return null;
    },
  },
};
</script>

<style lang="sass" scoped>
  a.disabled
    color: #757575
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  table
    width: 100%
  .align--end
    text-align: end !important
  .blank-column
    width: 100px
  .border--left
    border-left: 1px solid #DEE2E6
  .cell-content
    font-size: 16px
    font-weight: bold
    text-align: left
  .cell-content-no-data
    font-size: 16px
    font-weight: 500
  .compare-panel
    max-width: 320px
    min-width: 320px
    padding: 1rem
  .compare-panel-field-cell
    padding-left: 1rem
  .compare-panel-heading-bg-color
    font-size: 12px
    background-color: #EEEEEE
  .data-column
    width: 470px
  .icon-cell
    border: 0
    color: #212529
    text-align: center !important
  .label-newid
    background-color: #428316
    border: 0
    color: #FFFFFF
    text-align: center !important
  .label-retain
    border: 0
    color: #FFFFFF
    text-align: center !important
  .outer
    margin-left: auto
    overflow-x: auto
  .panel--highlighted
    background-color: #FFF5D6
  .right-column-icons
    display: flex
    height: 42px
    justify-content: space-between
    align-items: center
    border: 0
  .state-id
    color: #343A40
    font-size: 16px
  .state-id-label
    color: #737373
    font-size: 12px
  .static-bottom
    bottom: 0
    position: sticky
  .submission-header
    color: #737373
    font-size: 12px
  .switch-label
    font-weight: bold
    text-align: center
    vertical-align: middle
  .table
    tbody
      tr
        &:last-child
          border-bottom: 0
  .text-align--center
    text-align: center
  .user-actions
    margin-right: 1rem
    background: #FFF
    border-top: 1px solid #DEE2E6
    bottom: 0
    color: #F1F1F1
    padding: 10px 16px
    box-shadow: 0 2px 2px 0 #ccc
    position: sticky
  .vertical-align--bottom
    vertical-align: bottom
  .vertical-align--top
    vertical-align: top
</style>
